import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealOnScroll } from "./Reveal";
import { MANIFESTO } from "@/constants/testIds";
import { MANIFESTO_CHAPTERS } from "@/data/siteData";

const Chapter = ({ chapter, index }) => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], [80, -80]);
    const numX = useTransform(scrollYProgress, [0, 1], [40, -40]);

    const reverse = index % 2 === 1;

    return (
        <div
            ref={ref}
            data-testid={MANIFESTO.chapter(chapter.id)}
            className="relative grid md:grid-cols-12 gap-6 md:gap-10 items-center py-16 md:py-24 border-b-2 border-[#0a0a0a]"
        >
            {/* Massive outline number */}
            <motion.div
                style={{ x: numX }}
                className={`absolute ${
                    reverse ? "right-0" : "left-0"
                } top-4 pointer-events-none select-none z-0 opacity-40`}
            >
                <span className="font-display outline-text text-[28vw] md:text-[22vw] lg:text-[18vw] leading-none">
                    {chapter.id}
                </span>
            </motion.div>

            <div
                className={`relative z-10 md:col-span-5 ${
                    reverse ? "md:order-2" : "md:order-1"
                }`}
            >
                <motion.div
                    style={{ y: imgY }}
                    className="clip-arch border-2 border-[#0a0a0a] shadow-[10px_10px_0_0_#0a0a0a] overflow-hidden aspect-[3/4]"
                >
                    <img
                        src={chapter.image}
                        alt={chapter.title}
                        className="w-full h-full object-cover"
                    />
                </motion.div>
            </div>

            <div
                className={`relative z-10 md:col-span-6 ${
                    reverse ? "md:order-1 md:col-start-1" : "md:order-2 md:col-start-7"
                }`}
            >
                <RevealOnScroll>
                    <span className="inline-block text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-[#E63946]">
                        Capitolo {chapter.id} / 03
                    </span>
                </RevealOnScroll>
                <RevealOnScroll delay={0.1}>
                    <h3 className="font-display uppercase text-6xl md:text-7xl lg:text-8xl leading-[0.9] mt-3 md:mt-4">
                        {chapter.title}
                    </h3>
                </RevealOnScroll>
                <RevealOnScroll delay={0.2}>
                    <p className="mt-5 md:mt-6 text-lg md:text-xl leading-relaxed max-w-xl text-[#0a0a0a]/80">
                        {chapter.text}
                    </p>
                </RevealOnScroll>
            </div>
        </div>
    );
};

const Manifesto = () => {
    return (
        <section
            id="manifesto"
            data-testid={MANIFESTO.section}
            className="relative px-5 md:px-10 lg:px-14 max-w-[1600px] mx-auto py-20 md:py-28"
        >
            <div className="mb-12 md:mb-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6 border-b-2 border-[#0a0a0a] pb-8">
                <div>
                    <span className="text-xs md:text-sm uppercase tracking-[0.3em] font-bold text-[#E63946]">
                        · Il Manifesto
                    </span>
                    <h2 className="font-display uppercase text-5xl md:text-7xl leading-[0.9] mt-3">
                        Tre ingredienti,<br />
                        <span className="italic font-serif-editorial text-[#E63946] normal-case">
                            una direzione.
                        </span>
                    </h2>
                </div>
                <p className="max-w-md text-base md:text-lg text-[#0a0a0a]/70">
                    Dalla farina agli ingredienti, dal banco al cartone: ogni passaggio
                    è pensato per portarti un solo risultato — una pizza che sa di casa
                    e ti fa tornare.
                </p>
            </div>

            {MANIFESTO_CHAPTERS.map((c, i) => (
                <Chapter key={c.id} chapter={c} index={i} />
            ))}
        </section>
    );
};

export default Manifesto;
